/**
 * Переключатель организации в шапке кабинета (multi-organization-login): название активной
 * организации — кнопка, открывающая список организаций входа с поиском. Показывается, только
 * когда за входом закреплено больше одной организации.
 */
import { useState, type ReactNode } from 'react'
import { ChevronsUpDown, X } from 'lucide-react'
import { Card } from '@/design-system/components/layout/card'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '@/design-system/components/primitives/dialog'
import { OrganizationPicker } from './organization-picker'
import { useAuth } from './auth-context'

export function OrganizationSwitcher({ children }: { children: ReactNode }) {
  const { context, selectOrganization } = useAuth()
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          title="Сменить организацию"
          aria-label="Сменить организацию"
          className="flex items-center gap-2.5 rounded-8 p-1 text-left outline-none transition-colors hover:bg-hover focus-visible:ring-2 focus-visible:ring-accent"
        >
          {children}
          <ChevronsUpDown className="size-4 shrink-0 stroke-current text-txt-secondary" aria-hidden />
        </button>
      </DialogTrigger>
      <DialogContent title="Выбор организации" description="Выберите организацию, в которую хотите перейти">
        <Card
          header={
            <div className="flex items-center justify-between gap-3">
              <span>Выберите организацию</span>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Закрыть"
                  className="-mr-1 rounded-8 p-1 text-txt-secondary transition-colors hover:bg-hover hover:text-txt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="size-4 stroke-current" aria-hidden />
                </button>
              </DialogClose>
            </div>
          }
        >
          <OrganizationPicker
            onSelect={async (id) => {
              // Текущая организация: перезагружать кабинет незачем — просто закрываем список.
              if (id === context?.user.org?.id) {
                setOpen(false)
                return
              }
              await selectOrganization(id)
            }}
          />
        </Card>
      </DialogContent>
    </Dialog>
  )
}
